import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetListRealisasiV2,
  useAPIPelaksanaanProgress,
} from 'api/kinerja/useAPIKinerja'
import PanelSummary from 'app/KinerjaPage/components/PanelSummary'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { formatDate } from 'utils/datetime'
import DialogConfirmEdit from './components/DialogConfirmEdit'
import { getBadgeByStateV2, getCtaByState } from './utils'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'

const LoadingState = () => {
  return (
    <div className="flex justify-center p-6" data-testid="loading-spinner">
      <Spinner />
    </div>
  )
}

type TugasTambahanProps = {
  overridePeriodPayload?: boolean
  isViewOnly?: boolean
  period?: number
  year?: number
  resolveBuktiDukungUrl?: (id: string, sequence: number) => string
}

const defaultBuktiDukungUrlResolver = (id: string, sequence: number) =>
  `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/tugas-tambahan/${id}/bukti-dukung?sequence=${sequence}`

const TugasTambahan = ({
  isViewOnly = false,
  overridePeriodPayload = false,
  period,
  year,
  resolveBuktiDukungUrl = defaultBuktiDukungUrlResolver,
}: TugasTambahanProps) => {
  const periodParams = overridePeriodPayload
    ? { skp_period: period, skp_year: year }
    : undefined

  const { push } = useRouter()
  const { data, isLoading } = useAPIGetListRealisasiV2('TT', periodParams)
  const { data: progressData } = useAPIPelaksanaanProgress({ periodParams })
  const currentPeriod = progressData?.data?.period ?? 1

  const isEvaluated = progressData?.data?.evaluationDocumentState === 'READY'

  const dataKinerjaOrdered = data?.data ?? []
  const kinerjaUpdatedDate = data?.kinerjaUpdated?.find(
    (e) => e.kategori === 'TT'
  )

  const [showConfirmEditRHK, setShowConfirmEditRHK] = useState(false)

  return (
    <div className="px-8 pb-8 pt-12">
      <div className="max-w-7xl mx-auto">
        <Header
          title="Tugas Tambahan"
          className="mb-9"
          rightNode={
            <PanelSummary
              items={[
                {
                  label: 'Semester',
                  value: currentPeriod,
                },
              ]}
            />
          }
        />

        <div className="mt-12 bg-white rounded-lg p-6">
          <div className="flex">
            <div className="flex flex-col gap-1 flex-1">
              <Text variant="heading-md">Progres pengumpulan dokumen</Text>
              <Text variant="body" color="subdued">
                Kumpulkan berdasarkan Perencanaan Kinerja yang Anda dan atasan
                sudah sepakati.
              </Text>
            </div>

            <Button
              color="white"
              disabled={isEvaluated || isViewOnly}
              onClick={() => {
                if (!isEvaluated) {
                  setShowConfirmEditRHK(true)
                }
              }}
            >
              Ubah Rencana Hasil Kerja
            </Button>
          </div>

          {/* Tugas tambahan */}
          {isLoading ? (
            <LoadingState />
          ) : (
            <>
              {!!dataKinerjaOrdered.length && (
                <>
                  <TablePelaksanaanKinerja data-testid="table-tambahan">
                    <TableHeader>
                      <TableHeaderItem className="w-5">No.</TableHeaderItem>
                      <TableHeaderItem className="pl-14 w-[500px]">
                        Rencana Hasil Kerja
                      </TableHeaderItem>
                      <TableHeaderItem>Status</TableHeaderItem>
                      <TableHeaderItem />
                    </TableHeader>

                    <TableBody>
                      {dataKinerjaOrdered.map((kinerja, index) => (
                        <TableRow key={`row-kinerja-${kinerja.id}`}>
                          <TableRowItem className="text-center">
                            {index + 1}
                          </TableRowItem>
                          <TableRowItem className="pl-14">
                            {kinerja.deskripsi}
                          </TableRowItem>
                          <TableRowItem>
                            {getBadgeByStateV2(kinerja.state)}
                          </TableRowItem>
                          <TableRowItem>
                            <Link
                              href={resolveBuktiDukungUrl(
                                kinerja.id,
                                index + 1
                              )}
                              passHref
                            >
                              {getCtaByState(kinerja.state, isViewOnly)}
                            </Link>
                          </TableRowItem>
                        </TableRow>
                      ))}
                    </TableBody>
                  </TablePelaksanaanKinerja>
                </>
              )}
            </>
          )}
        </div>
        {kinerjaUpdatedDate && (
          <div className="mt-5 bg-white px-3 py-1 flex justify-center rounded-md">
            <Text variant="body" color="subdued">
              Rencana Hasil Kerja diubah pada:{' '}
              {formatDate(kinerjaUpdatedDate.kinerjaUpdatedAt, 'd MMMM yyyy')}
            </Text>
          </div>
        )}

        <DialogConfirmEdit
          data-testid="dialog-confirm-edit-rhk"
          customSize={595}
          isOpen={showConfirmEditRHK}
          onClose={() => setShowConfirmEditRHK(false)}
          onConfirm={() => {
            push(
              `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/tugas-tambahan/edit`
            )
          }}
        />
      </div>
    </div>
  )
}

export default TugasTambahan
